// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowBackward from "@store/Library/Components/Toolkit/Icons/ArrowBackward";
import OrderActionSet, {
    orderActionSetFieldDefinitions,
    OrderActionSetPresentationProps,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import constants from "@store/constants";
import getOrderChangeRequestAvailability from "@store/Framework/Common/Utilities/OrderChangeRequest/getOrderChangeRequestAvailability";
import submitRequestOrderChange from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/SubmitRequestOrderChange";
import OrderChangeRequestProbabilityAlert from "@store/Library/Components/OrderChangeRequest/OrderChangeRequestProbabilityAlert";
import { orderChangeRequestActionsStyles } from "@store/Library/Widgets/OrderChangeRequest/OrderChangeRequestActions";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import React, { FC, useContext } from "react";

const enum fields {
    showBack = "showBack",
    showProbabilityAlert = "showProbabilityAlert",
    showSubmit = "showSubmit",
}

export interface OrderChangeRequestReviewActionsProps extends WidgetProps {
    fields: {
        [fields.showBack]?: boolean;
        [fields.showProbabilityAlert]?: boolean;
        [fields.showSubmit]?: boolean;
    };
}

export interface OrderChangeRequestReviewActionsStyles {
    alertWrapper?: InjectableCss;
    backButton?: ButtonPresentationProps;
    backLink?: LinkPresentationProps;
    orderActionSet?: OrderActionSetPresentationProps;
    submitButton?: ButtonPresentationProps;
    submitLink?: LinkPresentationProps;
}

export const OrderChangeRequestReviewActionsStyles: OrderChangeRequestReviewActionsStyles = {
    orderActionSet: orderChangeRequestActionsStyles.orderActionSet,
    alertWrapper: orderChangeRequestActionsStyles.alertWrapper,
    submitButton: { variant: "primary" },
    backButton: {
        variant: "tertiary",
        icon: {
            position: "left",
            src: ArrowBackward,
        },
    },
};

const styles = OrderChangeRequestReviewActionsStyles;

const OrderChangeRequestReviewActions: FC<OrderChangeRequestReviewActionsProps> = ({ fields: widgetFields }) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { value: order } = useContext(OrderStateContext);
    const { changeRequestOrder, isSubmitting } = useAppSelector(state => state.greif.pages.orderChange);
    const backPageLink = usePageLinkByPageType(constants.pageType.orderChangeRequest);
    const { canRequestOrderChange } = getOrderChangeRequestAvailability(order);

    if (!order) {
        return null;
    }

    return (
        <OrderActionSet
            {...styles.orderActionSet}
            fields={widgetFields}
            actions={[
                {
                    button: (
                        <StyledWrapper
                            {...styles.alertWrapper}
                            className="alert"
                            data-test-selector="orderChangeRequestProbabilityAlert"
                        >
                            <OrderChangeRequestProbabilityAlert key="probabilityAlert" />
                        </StyledWrapper>
                    ),
                    field: fields.showProbabilityAlert,
                },
                {
                    key: "submit",
                    label: translate("Send Change Request"),
                    onClick: () => {
                        if (canRequestOrderChange) {
                            dispatch(submitRequestOrderChange(changeRequestOrder));
                            sessionStorage.removeItem(changeRequestOrder.orderId);
                        }
                    },
                    buttonStyles: styles.submitButton,
                    linkStyles: styles.submitLink,
                    field: fields.showSubmit,
                    isLoading: isSubmitting,
                    isDisabled: isSubmitting || !canRequestOrderChange,
                    testSelector: "requestOrderChangeHeader_send",
                },
                {
                    key: "back",
                    label: translate("Back to edit"),
                    onClick: () => {
                        if (backPageLink?.url) {
                            const orderNumber = order.erpOrderNumber || order.webOrderNumber;
                            history.push(`${backPageLink.url}?orderNumber=${encodeURIComponent(orderNumber)}`);
                        }
                    },
                    buttonStyles: styles.backButton,
                    linkStyles: styles.backLink,
                    isDisabled: !backPageLink?.url,
                    field: fields.showBack,
                    testSelector: "orderChangeRequestReviewActions_back",
                },
            ]}
        />
    );
};

const widgetModule: WidgetModule = {
    component: OrderChangeRequestReviewActions,
    definition: {
        group: constants.widgetGroup.orderChangeRequestReview,
        displayName: "Order Change Request Review Actions",
        allowedContexts: [constants.pageType.orderChangeRequestReview],
        fieldDefinitions: [
            ...orderActionSetFieldDefinitions([
                { name: fields.showProbabilityAlert, defaultState: "sticky" },
                { name: fields.showSubmit, defaultState: "sticky" },
                { name: fields.showBack, defaultState: "sticky" },
            ]),
        ],
    },
};

export default widgetModule;
